import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { EditOutlined } from '@ant-design/icons';
import { Dropdown as AntDDropdown, Form } from 'antd';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import styled from 'styled-components';

import {
  deleteActionPlan,
  deleteActionPlanStep,
  getActionPlanDetails,
  updateActionPlanStep,
} from 'apis/ActionPlansAPI';
import { getAIFeedbacks } from 'apis/FeedbackAPI';
import DownCaretIcon from 'common/Icons/DownCaretIcon';
import ConfirmModal from 'common/Modal/ConfirmModal';
import { StyledTable } from 'common/Table/StyledTable';
import { AI_FEEDBACK_FEATURE_TYPES } from 'components/Feedback/constants';
import Feedback from 'components/Feedback/Feedback';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import ErrorMessage from 'components/lib/ErrorMessage';
import { ACTION_PLAN_SOURCE } from 'constants/actionPlan';
import { useAppSelector } from 'store/hook';
import { ActionPlanDetailsType, ActionPlanStepListItemType } from 'types/actionPlans';

import ActionPlanComments from './ActionPlanComments';
import ActionPlanDeleteWithReasonModal from './ActionPlanDeleteWithReasonModal';
import ActionPlanDetailsProgress from './ActionPlanDetailsProgress';
import ActionPlanDetailsTitleCard from './ActionPlanDetailsTitleCard';
import { saveActionPlanForm } from './actionPlanFormSave';
import {
  getActionPlanActorName,
  getActionPlanSourceLabel,
  isAIGeneratedValue,
  isEditedActionPlanStep,
} from './actionPlanMetaUtils';
import ActionPlansHeader from './ActionPlansHeader';
import ActionPlanStatusSelector from './ActionPlanStatusSelector';
import {
  ACTION_PLAN_SAVE_ERROR_MESSAGE,
  ACTION_PLAN_STATUS,
  ACTION_PLAN_STATUS_BG_COLOR_MAP,
  ACTION_PLAN_STATUS_OPTIONS,
} from './constants';
import CreateEditActionPlanModal, { ActionPlanFormValues } from './CreateEditActionPlanModal';
import { SelectOption } from './CreateEditActionPlansModalTypes';
import ActionPlanEmailCampaign from './EmailCampaign/ActionPlanEmailCampaign';
import { buildItemTypeOptions } from './formUtils';
import OwnerInfo from './OwnerInfo';
import useActionPlanFocusAreas from './useActionPlanFocusAreas';
import useActionPlanOwnerOptions from './useActionPlanOwnerOptions';
import useActionPlansConfirmModal from './useActionPlansConfirmModal';
import { getCurrentUserDisplayName, isFormValidationError, sortActionPlanSteps, toNumberOrUndefined } from './utils';
import { GLOBAL_DATE_FORMAT } from '../../constants';

const Container = styled.div`
  width: 100%;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 10px 30px;
`;

const TableWrapper = styled.div`
  position: relative;
  background: var(--background-gray-light);
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  padding: 20px;

  .ant-table-thead > tr > th {
    background-color: var(--background-gray-light) !important;
    border-color: #d7d7d7 !important;
  }

  .ant-table-thead > tr > th:first-child {
    padding-left: 25px !important;
  }

  .ant-table-thead > tr > th.ant-table-cell::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    border-color: var(--line-gray) !important;
    height: 60px;
    vertical-align: middle;
  }

  .ant-table-tbody > tr > td:first-child {
    border-left: 1px solid var(--line-gray);
    padding-left: 20px;
  }

  .ant-table-tbody > tr > td:last-child {
    border-right: 1px solid var(--line-gray);
    padding-right: 20px !important;
  }

  .actions-hover-reveal {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  .ant-table-tbody > tr:hover .actions-hover-reveal {
    opacity: 1;
    pointer-events: auto;
  }
`;

const TableHeaderActions = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
`;

const ActionCell = styled.div`
  min-width: 0;
  padding-left: 10px;
  font-family: var(--font-bold);
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
`;

const ItemMeta = styled.div`
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
`;

const ItemMetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
`;

const ItemModified = styled.div`
  font-size: 11px;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ActionItemActionsDropdownMenu = styled.div`
  .ant-dropdown-menu {
    padding: 0;
    border-radius: 0;
    border: 1px solid var(--line-gray);
    border-bottom: 0;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    min-width: 200px;
    width: max-content;
  }

  .ant-dropdown-menu-item {
    font-size: 14px;
    padding: 6px 10px;
    border-bottom: 1px solid var(--line-gray);
    white-space: nowrap;
  }
`;

const ActionPlanDetails = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useRootContext();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { actionPlanId } = useParams();
  const actionPlanIdNumber = Number(actionPlanId);
  const isValidActionPlanId = Number.isFinite(actionPlanIdNumber) && actionPlanIdNumber > 0;

  const [planEditOpen, setPlanEditOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [aiDeleteOpen, setAiDeleteOpen] = useState(false);

  const [planForm] = Form.useForm<ActionPlanFormValues>();
  const { openConfirm, confirmModalProps } = useActionPlansConfirmModal();
  const currentUserDisplayName = getCurrentUserDisplayName(currentUser);

  const { data, isLoading, isError } = useQuery<ActionPlanDetailsType>({
    queryKey: ['action-plan-steps', actionPlanId],
    queryFn: () => getActionPlanDetails(actionPlanIdNumber),
    enabled: isValidActionPlanId,
    refetchOnWindowFocus: false,
  });

  const { data: feedback } = useQuery({
    queryKey: ['ai-feedbacks', 'action-plan', actionPlanIdNumber, selectedCommunity?.id],
    queryFn: () => getAIFeedbacks({ action_plan_id: actionPlanIdNumber, community_id: selectedCommunity?.id }),
    select: (feedbacks) => feedbacks?.[0] ?? null,
    enabled: isValidActionPlanId && Boolean(selectedCommunity?.id),
    refetchOnWindowFocus: false,
  });

  const ownerOptions = useActionPlanOwnerOptions(selectedCommunity?.company?.id, data);

  const { focusAreaOptions } = useActionPlanFocusAreas();

  const itemTypeOptions = useMemo<SelectOption[]>(() => buildItemTypeOptions(data), [data]);

  const invalidateAll = () => {
    queryClient.invalidateQueries(['action-plan-steps', actionPlanId]);
    queryClient.invalidateQueries(['action-plans', selectedCommunity?.id]);
  };

  const handlePlanDeleted = () => {
    setPlanEditOpen(false);
    planForm.resetFields();
    queryClient.cancelQueries(['action-plan-steps', actionPlanId]);
    queryClient.removeQueries(['action-plan-steps', actionPlanId]);
    queryClient.invalidateQueries(['action-plans', selectedCommunity?.id]);
    navigate('..', { relative: 'path' });
  };

  const steps = useMemo(() => sortActionPlanSteps(data?.steps), [data?.steps]);
  const completedItemsCount = steps.filter((item) => item.status === ACTION_PLAN_STATUS.COMPLETE).length;
  const totalItemsCount = steps.length;
  const completionProgress = totalItemsCount > 0 ? (completedItemsCount / totalItemsCount) * 100 : 0;
  const createdAtLabel = data?.created_at ? `Created ${format(new Date(data.created_at), 'MMM d yyyy')}` : '';
  const isEmailCampaignVisible =
    Boolean(data?.has_email_campaign) && data?.source === ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE;
  const showComments = isValidActionPlanId && !currentUser?.isSuperuser;

  const { mutateAsync: assignStepToMe } = useMutation({
    mutationFn: (actionPlanStepId: number) => {
      if (!currentUser) {
        return Promise.resolve(null);
      }
      return updateActionPlanStep(actionPlanStepId, { owner_id: currentUser.user_id });
    },
    onSuccess: invalidateAll,
  });

  const { mutateAsync: updateStepStatus } = useMutation({
    mutationFn: ({ actionPlanStepId, status }: { actionPlanStepId: number; status: number }) =>
      updateActionPlanStep(actionPlanStepId, { status }),
    onSuccess: invalidateAll,
  });

  const { mutateAsync: removeStep } = useMutation({
    mutationFn: (actionPlanStepId: number) => deleteActionPlanStep(actionPlanStepId),
    onSuccess: invalidateAll,
  });

  const { mutateAsync: savePlanEdit, isLoading: isSavingPlanEdit } = useMutation({
    mutationFn: async (values: ActionPlanFormValues) => {
      if (!data) {
        return;
      }

      await saveActionPlanForm({
        actionPlanId: actionPlanIdNumber,
        values,
        currentDetails: data,
      });
    },
    onSuccess: () => {
      setPlanEditOpen(false);
      planForm.resetFields();
      invalidateAll();
    },
  });

  const { mutateAsync: removePlan, isLoading: isRemovingPlan } = useMutation({
    mutationFn: (reason?: string) =>
      deleteActionPlan(actionPlanIdNumber, reason?.trim() ? { archived_reason: reason.trim() } : undefined),
    onSuccess: handlePlanDeleted,
  });

  const sourceNeedsDeleteReason =
    data?.source === ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE || data?.source === ACTION_PLAN_SOURCE.ALERTS;

  const openEditPlanModal = () => {
    if (!data) {
      return;
    }

    planForm.setFieldsValue({
      name: data.name,
      description: data.description,
      focus_area_id: toNumberOrUndefined(data.focus_area_id),
      owner_id: toNumberOrUndefined(data.owner?.id),
      due_date: data.due_date ? dayjs(data.due_date) : undefined,
      items: steps.map((step) => ({
        id: toNumberOrUndefined(step.id),
        action: step.action,
        type_id: toNumberOrUndefined(step.type_id),
        owner_id: toNumberOrUndefined(step.owner?.id),
        due_date: step.due_date ? dayjs(step.due_date) : undefined,
      })),
    });
    setPlanEditOpen(true);
  };

  const columns = [
    {
      title: 'ACTION ITEM',
      dataIndex: 'action',
      key: 'action',
      width: 360,
      render: (value: ActionPlanStepListItemType['action'], record: ActionPlanStepListItemType) => {
        const aiGenerated = isAIGeneratedValue(record.is_ai_generated);
        const createdByName = getActionPlanActorName(record.created_by);
        const sourceLabel = getActionPlanSourceLabel(aiGenerated, createdByName, currentUserDisplayName, record.source);
        const isEdited = isEditedActionPlanStep(record);
        return (
          <ActionCell>
            {value}
            <ItemMetaRow>
              <ItemMeta>{sourceLabel}</ItemMeta>
              {isEdited && (
                <ItemModified>
                  <EditOutlined />
                  Edited
                </ItemModified>
              )}
            </ItemMetaRow>
          </ActionCell>
        );
      },
    },
    {
      title: 'DEPARTMENT',
      dataIndex: 'type_name',
      key: 'type_name',
      width: 130,
      render: (value: ActionPlanStepListItemType['type_name']) => value || '-',
    },
    {
      title: 'ITEM OWNER',
      dataIndex: 'owner',
      key: 'owner',
      width: 180,
      render: (_value: ActionPlanStepListItemType['owner'], record: ActionPlanStepListItemType) => (
        <OwnerInfo
          name={record.owner?.name ?? null}
          avatarUrl={record.owner?.avatar_url ?? null}
          showAssignToMe={!record.owner && Boolean(currentUser)}
          onAssignToMe={async (event) => {
            event.stopPropagation();
            await assignStepToMe(record.id);
          }}
        />
      ),
    },
    {
      title: 'DUE',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 110,
      render: (value: ActionPlanStepListItemType['due_date']) =>
        value ? dayjs(value).format(GLOBAL_DATE_FORMAT) : '-',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value: ActionPlanStepListItemType['status'], record: ActionPlanStepListItemType) => (
        <ActionPlanStatusSelector
          value={value}
          options={ACTION_PLAN_STATUS_OPTIONS}
          statusColorMap={ACTION_PLAN_STATUS_BG_COLOR_MAP}
          dataTestId="action-plan-details-status-selector"
          onChange={async (status) => {
            if (status === value) {
              return;
            }
            await updateStepStatus({ actionPlanStepId: record.id, status });
          }}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_value: unknown, record: ActionPlanStepListItemType) => (
        <ActionsCell className="actions-hover-reveal" onClick={(event) => event.stopPropagation()}>
          <AntDDropdown
            dropdownRender={(menu) => <ActionItemActionsDropdownMenu>{menu}</ActionItemActionsDropdownMenu>}
            menu={{
              items: [
                {
                  key: 'delete-item',
                  label: 'Delete Action Item',
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    openConfirm(
                      'Delete Action Item?',
                      `You are about to delete the action item '${record.action}'. This cannot be undone.`,
                      () => removeStep(record.id),
                      'Delete'
                    );
                  },
                },
              ],
            }}
          >
            <Button onClick={(event) => event.stopPropagation()}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                Actions
                <DownCaretIcon />
              </span>
            </Button>
          </AntDDropdown>
        </ActionsCell>
      ),
    },
  ];

  return (
    <Container data-test-id="action-plan-details-page">
      <ActionPlansHeader
        rightSlot={
          isValidActionPlanId ? (
            <Feedback
              subtitle={data?.name || ''}
              featureType={AI_FEEDBACK_FEATURE_TYPES.ACTION_PLAN}
              actionPlan={actionPlanIdNumber}
              sourceUrl={window.location.href}
              existingFeedback={feedback}
            />
          ) : undefined
        }
      />
      <Main data-test-id="action-plan-details-main">
        <div data-test-id="action-plan-details-error-message">
          <ErrorMessage text="Failed to load action plan steps. Please try again." active={isError} />
        </div>
        <ActionPlanDetailsTitleCard
          actionPlan={data}
          createdAtLabel={createdAtLabel}
          fallbackCreatedByName={currentUserDisplayName}
          onBack={() => navigate('..', { relative: 'path' })}
        />
        {isEmailCampaignVisible ? <ActionPlanEmailCampaign actionPlan={data} /> : null}
        <TableWrapper data-test-id="action-plan-details-table-wrapper">
          <TableHeaderActions>
            <Button onClick={() => openEditPlanModal()}>
              <EditOutlined /> Edit Plan
            </Button>
          </TableHeaderActions>
          <ActionPlanDetailsProgress
            completedItemsCount={completedItemsCount}
            totalItemsCount={totalItemsCount}
            completionProgress={completionProgress}
          />
          <div data-test-id="action-plan-details-table">
            <StyledTable
              columns={columns as any}
              size={'small'}
              dataSource={steps}
              bordered={false}
              loading={isLoading}
              rowKey="id"
              pagination={false}
              tableLayout="fixed"
              scroll={{ x: 1020 }}
            />
          </div>
        </TableWrapper>
        {showComments ? <ActionPlanComments actionPlanId={actionPlanIdNumber} /> : null}
      </Main>

      <CreateEditActionPlanModal
        categoryOptions={focusAreaOptions}
        itemTypeOptions={itemTypeOptions}
        open={planEditOpen}
        isEditing
        isSaving={isSavingPlanEdit}
        form={planForm}
        ownerOptions={ownerOptions}
        onCancel={() => {
          setPlanEditOpen(false);
          planForm.resetFields();
        }}
        onSubmit={async () => {
          if (isSavingPlanEdit) {
            return;
          }

          try {
            const values = await planForm.validateFields();
            await savePlanEdit(values);
          } catch (error) {
            if (isFormValidationError(error)) {
              return;
            }

            showMessage('error', ACTION_PLAN_SAVE_ERROR_MESSAGE);
          }
        }}
        onDelete={() => {
          if (sourceNeedsDeleteReason) {
            setDeleteReason('');
            setAiDeleteOpen(true);
            return;
          }

          openConfirm(
            'Delete Action Plan?',
            `You are about to delete the action plan '${data?.name ?? ''}'. This cannot be undone.`,
            () => removePlan(undefined)
          );
        }}
      />
      <ConfirmModal {...confirmModalProps} />
      <ActionPlanDeleteWithReasonModal
        open={aiDeleteOpen}
        planName={data?.name ?? ''}
        loading={isRemovingPlan}
        reason={deleteReason}
        onReasonChange={setDeleteReason}
        onConfirm={async () => {
          await removePlan(deleteReason);
          setDeleteReason('');
          setAiDeleteOpen(false);
        }}
        onCancel={() => {
          if (isRemovingPlan) {
            return;
          }
          setDeleteReason('');
          setAiDeleteOpen(false);
        }}
      />
    </Container>
  );
};

export default ActionPlanDetails;
